import * as THREE from 'three';
import { PALETTE } from '../voxelColor';
import { VoxelAsset } from '../voxelTypes';

export type CharacterType = 'farmer' | 'miller' | 'villager';

export interface VoxelDataSmall {
  x: number;
  y: number;
  z: number;
  color: number;
}

export function cleanupDuplicates(voxels: VoxelDataSmall[]): VoxelDataSmall[] {
  const result: VoxelDataSmall[] = [];
  const seen = new Set<string>();
  // Iterate backwards to keep the last-added voxel at any coordinate
  for (let i = voxels.length - 1; i >= 0; i--) {
    const v = voxels[i]!;
    const key = `${v.x},${v.y},${v.z}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.unshift(v);
    }
  }
  return result;
}

export interface CharacterParts {
  head: VoxelDataSmall[];
  torso: VoxelDataSmall[];
  leftArm: VoxelDataSmall[];
  rightArm: VoxelDataSmall[];
  leftLeg: VoxelDataSmall[];
  rightLeg: VoxelDataSmall[];
}

export function generateCharacterParts(type: CharacterType): CharacterParts {
  const head: VoxelDataSmall[] = [];
  const torso: VoxelDataSmall[] = [];
  const leftArm: VoxelDataSmall[] = [];
  const rightArm: VoxelDataSmall[] = [];
  const leftLeg: VoxelDataSmall[] = [];
  const rightLeg: VoxelDataSmall[] = [];

  const addBoxToPart = (part: VoxelDataSmall[], ox: number, oy: number, oz: number, w: number, h: number, d: number, color: number) => {
    const hw = Math.floor(w/2);
    const hd = Math.floor(d/2);
    for (let x = -hw; x < -hw + w; x++) {
      for (let y = 0; y < h; y++) {
        for (let z = -hd; z < -hd + d; z++) {
          part.push({ x: ox + x, y: oy + y, z: oz + z, color });
        }
      }
    }
  };

  const addVoxelToPart = (part: VoxelDataSmall[], x: number, y: number, z: number, color: number) => {
    part.push({ x, y, z, color });
  };

  const skin = PALETTE.WOOD_OAK_LIGHT;
  const eye = PALETTE.STONE_DARK;
  const mouthColor = 0x8b0000;
  const browColor = 0x221100;
  const shadowSkinColor = skin; // Remove the light tint by making it identical to main skin color

  if (type === 'farmer') {
    const pants = PALETTE.WATER_CYAN_DEEP; // Denim Overalls
    const shirt = PALETTE.FLOWER_RED; // Plaid shirt
    const hat = PALETTE.THATCH_YELLOW; // Straw hat
    const shoe = PALETTE.WOOD_OAK_DARK;

    // --- LEGS & FEET (Pivot at hip y=0, goes down to y=-6) ---
    // Left Leg
    addBoxToPart(leftLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(leftLeg, 0, -4, 0, 3, 4, 3, pants);
    // Right Leg
    addBoxToPart(rightLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(rightLeg, 0, -4, 0, 3, 4, 3, pants);

    // --- TORSO (Pivot at bottom y=0, goes up to y=5) ---
    addBoxToPart(torso, 0, 0, 0, 7, 5, 4, shirt); // Plaid shirt base
    addBoxToPart(torso, 0, 0, 2, 5, 4, 1, pants); // Overalls bib front
    addBoxToPart(torso, 0, 0, -2, 5, 4, 1, pants); // Overalls bib back
    // Overalls shoulder straps
    for (let y = 3; y <= 4; y++) {
      addVoxelToPart(torso, -2, y, 1, pants);
      addVoxelToPart(torso, 2, y, 1, pants);
      addVoxelToPart(torso, -2, y, -1, pants);
      addVoxelToPart(torso, 2, y, -1, pants);
    }
    // Plaid highlights
    for (let y = 0; y <= 4; y += 2) {
      addVoxelToPart(torso, -3, y, 0, PALETTE.FLOWER_YELLOW);
      addVoxelToPart(torso, 3, y, 0, PALETTE.FLOWER_YELLOW);
    }

    // --- ARMS (Pivot at shoulder y=0, goes down to y=-5) ---
    // Left Arm
    addBoxToPart(leftArm, 0, -3, 0, 2, 3, 2, shirt);
    addBoxToPart(leftArm, 0, -5, 0, 2, 2, 2, skin);
    // Right Arm (Garden Hoe will be attached in double resolution!)
    addBoxToPart(rightArm, 0, -3, 0, 2, 3, 2, shirt);
    addBoxToPart(rightArm, 0, -5, 0, 2, 2, 2, skin);

    // --- HEAD (Pivot at neck y=0, goes up to y=5+) ---
    addBoxToPart(head, 0, 0, 0, 6, 5, 5, skin); // Head base
    // FLAT FACIAL EXPRESSION (at z = 2, Minecraft style, no protruding!)
    // Eyes - Solid dark 1x1 squares
    addVoxelToPart(head, -2, 2, 2, eye);
    addVoxelToPart(head, 1, 2, 2, eye);
    // Eyebrows (focused, determined look)
    addVoxelToPart(head, -3, 4, 2, browColor);
    addVoxelToPart(head, -2, 4, 2, browColor);
    addVoxelToPart(head, 1, 4, 2, browColor);
    addVoxelToPart(head, 2, 4, 2, browColor);
    // Flat shaded nose texture
    addVoxelToPart(head, 0, 2, 2, shadowSkinColor);
    addVoxelToPart(head, -1, 2, 2, shadowSkinColor);
    // Confident straight smirk mouth
    addVoxelToPart(head, -1, 1, 2, mouthColor);
    addVoxelToPart(head, 0, 1, 2, mouthColor);

    // Straw Hat (attached to head)
    addBoxToPart(head, 0, 4, 0, 10, 1, 10, hat); // Brim
    addBoxToPart(head, 0, 5, 0, 6, 1, 6, PALETTE.WOOD_OAK_DARK); // Ribbon
    addBoxToPart(head, 0, 6, 0, 6, 2, 6, hat); // Hat top

  } else if (type === 'miller') {
    const pants = PALETTE.STONE_MEDIUM;
    const shirt = PALETTE.FLOWER_WHITE;
    const apron = 0xf5f5f0;
    const apronStraps = PALETTE.WOOD_OAK_DARK;
    const shoe = PALETTE.STONE_DARK;
    const hair = PALETTE.THATCH_YELLOW; // Blond hair
    const hat = PALETTE.FLOWER_WHITE;

    // --- LEGS & FEET ---
    // Left Leg
    addBoxToPart(leftLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(leftLeg, 0, -4, 0, 3, 4, 3, pants);
    // Right Leg
    addBoxToPart(rightLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(rightLeg, 0, -4, 0, 3, 4, 3, pants);

    // --- TORSO ---
    addBoxToPart(torso, 0, 0, 0, 7, 5, 4, shirt);
    addBoxToPart(torso, 0, -1, 2, 5, 6, 1, apron); // Apron front plate
    addVoxelToPart(torso, -2, 3, 2, apronStraps);
    addVoxelToPart(torso, 2, 3, 2, apronStraps);
    addBoxToPart(torso, 0, -1, -2, 5, 1, 1, apron); // Apron tie back

    // --- ARMS ---
    // Left Arm (Bread Loaf will be attached in double resolution!)
    addBoxToPart(leftArm, 0, -3, 0, 2, 3, 2, shirt);
    addBoxToPart(leftArm, 0, -5, 0, 2, 2, 2, skin);
    // Right Arm
    addBoxToPart(rightArm, 0, -3, 0, 2, 3, 2, shirt);
    addBoxToPart(rightArm, 0, -5, 0, 2, 2, 2, skin);

    // --- HEAD ---
    addBoxToPart(head, 0, 0, 0, 6, 5, 5, skin);
    // Hair
    addBoxToPart(head, -3, 2, -2, 1, 3, 4, hair);
    addBoxToPart(head, 2, 2, -2, 1, 3, 4, hair); // Fixed hair right side X position from 3 to 2 for perfect symmetry
    addBoxToPart(head, 0, 2, -3, 6, 3, 1, hair);
    // FLAT FACIAL EXPRESSION (at z = 2)
    // Blue eyes - Solid blue 1x1 squares
    addVoxelToPart(head, -2, 2, 2, 0x2b4c7e);
    addVoxelToPart(head, 1, 2, 2, 0x2b4c7e);
    // Blond friendly eyebrows
    addVoxelToPart(head, -3, 4, 2, 0xcca300);
    addVoxelToPart(head, -2, 4, 2, 0xcca300);
    addVoxelToPart(head, 1, 4, 2, 0xcca300);
    addVoxelToPart(head, 2, 4, 2, 0xcca300);
    // Flat shaded nose (symmetric 2-voxel nose just like the Farmer, to keep equal distance between eyes)
    addVoxelToPart(head, -1, 2, 2, shadowSkinColor);
    addVoxelToPart(head, 0, 2, 2, shadowSkinColor);
    // Jolly smile
    addVoxelToPart(head, -1, 1, 2, mouthColor);
    addVoxelToPart(head, 0, 1, 2, mouthColor);

    // Baker's Hat
    addBoxToPart(head, 0, 5, 0, 7, 1, 7, hat);
    addBoxToPart(head, 0, 6, 0, 8, 3, 8, hat);

  } else {
    // Villager girl
    const dressMain = PALETTE.FLOWER_YELLOW; // Golden yellow
    const dressTrim = PALETTE.FLOWER_WHITE;  // White lace
    const shoe = PALETTE.STONE_DARK;
    const hair = PALETTE.ROOF_RED_CLAY; // Auburn/Orange hair
    const bowColor = PALETTE.FLOWER_RED;

    // --- LEGS & FEET (matching the other characters, skin color bare legs) ---
    // Left Leg
    addBoxToPart(leftLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(leftLeg, 0, -4, 0, 3, 4, 3, skin);
    // Right Leg
    addBoxToPart(rightLeg, 0, -6, 1, 3, 2, 4, shoe);
    addBoxToPart(rightLeg, 0, -4, 0, 3, 4, 3, skin);

    // --- TORSO (matching width 7 of other characters, with white dress trim hem) ---
    addBoxToPart(torso, 0, 0, 0, 7, 5, 4, dressMain);
    addBoxToPart(torso, 0, 0, 0, 7, 1, 4, dressTrim);

    // --- ARMS ---
    // Left Arm
    addBoxToPart(leftArm, 0, -2, 0, 2, 2, 2, dressTrim); // puffed sleeve
    addBoxToPart(leftArm, 0, -5, 0, 2, 3, 2, skin);
    // Right Arm (Flower Basket will be attached in double resolution!)
    addBoxToPart(rightArm, 0, -2, 0, 2, 2, 2, dressTrim); // puffed sleeve
    addBoxToPart(rightArm, 0, -5, 0, 2, 3, 2, skin);

    // --- HEAD ---
    addBoxToPart(head, 0, 0, 0, 6, 5, 5, skin);
    // Hair base, bangs & side buns
    addBoxToPart(head, 0, 3, 0, 7, 2, 6, hair); // Top cap
    addBoxToPart(head, 0, 1, -2, 7, 3, 1, hair); // Back wrap
    addBoxToPart(head, -3, 0, 1, 1, 3, 2, hair); // Left bangs
    addBoxToPart(head, 3, 0, 1, 1, 3, 2, hair); // Right bangs
    addBoxToPart(head, -4, 2, 0, 2, 2, 2, hair); // Left hair bun
    addBoxToPart(head, 4, 2, 0, 2, 2, 2, hair); // Right hair bun
    addVoxelToPart(head, -3, 3, 1, bowColor); // Bows
    addVoxelToPart(head, 3, 3, 1, bowColor);

    // FLAT FACIAL EXPRESSION (at z = 2)
    // Sparkling emerald green eyes - Solid green 1x1 squares
    addVoxelToPart(head, -2, 2, 2, 0x1a4a3a);
    addVoxelToPart(head, 1, 2, 2, 0x1a4a3a);
    // Soft auburn eyebrows
    addVoxelToPart(head, -3, 4, 2, PALETTE.ROOF_RED_CLAY);
    addVoxelToPart(head, -2, 4, 2, PALETTE.ROOF_RED_CLAY);
    addVoxelToPart(head, 1, 4, 2, PALETTE.ROOF_RED_CLAY);
    addVoxelToPart(head, 2, 4, 2, PALETTE.ROOF_RED_CLAY);
    // Flat shaded nose
    addVoxelToPart(head, 0, 2, 2, shadowSkinColor);
    // Sweet red smile
    addVoxelToPart(head, -1, 1, 2, 0x9e2a2b);
    addVoxelToPart(head, 0, 1, 2, 0x9e2a2b);
  }

  // Ensure every part is cleaned from duplicate overlaps
  return {
    head: cleanupDuplicates(head),
    torso: cleanupDuplicates(torso),
    leftArm: cleanupDuplicates(leftArm),
    rightArm: cleanupDuplicates(rightArm),
    leftLeg: cleanupDuplicates(leftLeg),
    rightLeg: cleanupDuplicates(rightLeg),
  };
}

export function generateAccessoryVoxels(type: CharacterType): VoxelDataSmall[] {
  const voxels: VoxelDataSmall[] = [];
  
  if (type === 'farmer') {
    // --- HIGH-RESOLUTION GARDEN HOE ---
    // Handle (Wooden Shaft): nice vertical pole, centered at hand, extending up and down
    // Shaft coordinates: x=0, z=0, y from -14 to 10
    for (let y = -14; y <= 10; y++) {
      voxels.push({ x: 0, y, z: 0, color: PALETTE.WOOD_OAK_DARK });
    }
    // Metal bracket connecting shaft to hoe blade
    voxels.push({ x: 0, y: 11, z: 0, color: PALETTE.STONE_MEDIUM });
    voxels.push({ x: 0, y: 12, z: 0, color: PALETTE.STONE_MEDIUM });
    voxels.push({ x: 0, y: 12, z: 1, color: PALETTE.STONE_LIGHT });
    voxels.push({ x: 0, y: 12, z: 2, color: PALETTE.STONE_LIGHT });
    
    // Detailed hoe blade, wide and bending downwards
    // Blade from x = -3 to 3, y = 8 to 12, z = 3
    for (let x = -3; x <= 3; x++) {
      for (let y = 8; y <= 12; y++) {
        // give it a cool shape, thinner at top, wider at bottom
        voxels.push({ x, y, z: 3, color: PALETTE.STONE_DARK });
      }
    }
    // extra cutting edge details
    for (let x = -3; x <= 3; x++) {
      voxels.push({ x, y: 7, z: 3, color: PALETTE.STONE_MEDIUM });
    }
    
  } else if (type === 'miller') {
    // --- HIGH-RESOLUTION ARTISAN BREAD LOAF ---
    // Beautiful score marks on top (gold interior) and flour dusting on shoulders
    const crustColor = 0x8b5a2b;
    const innerBreadColor = 0xeedd82;
    const flourColor = 0xffffff;

    // Oblong bread loaf size: width=6, height=5, depth=9
    for (let x = -3; x <= 3; x++) {
      for (let y = -2; y <= 2; y++) {
        for (let z = -4; z <= 4; z++) {
          // round off corners for high-fidelity organic feel
          const distSq = (x * x) / 9 + (y * y) / 4 + (z * z) / 16;
          if (distSq > 1.3) continue;

          let color = crustColor;
          
          // Scores on the top surface (y = 2)
          if (y === 2 && (z === -2 || z === 1) && Math.abs(x) <= 1) {
            color = innerBreadColor; // golden crust crack
          } else if (y === 2 && Math.abs(x) === 2 && Math.abs(z) <= 2) {
            color = flourColor; // flour dusting
          }

          voxels.push({ x, y, z, color });
        }
      }
    }
    
  } else if (type === 'villager') {
    // --- HIGH-RESOLUTION FLOWER BASKET ---
    const basketColor = 0x8b5a2b;
    const rimColor = 0xcd853f;
    const leafColor = 0x228b22;
    
    // Basket body: round bowl centered around x=0, z=0, y from -4 to 0
    for (let x = -3; x <= 3; x++) {
      for (let z = -3; z <= 3; z++) {
        for (let y = -4; y <= 0; y++) {
          const isCorner = Math.abs(x) === 3 && Math.abs(z) === 3;
          if (isCorner) continue;
          
          const isWall = Math.abs(x) === 3 || Math.abs(z) === 3 || (Math.abs(x) === 2 && Math.abs(z) === 2 && y <= -2);
          const isBottom = y === -4;
          
          if (isWall || isBottom) {
            const color = (y === 0) ? rimColor : basketColor;
            voxels.push({ x, y, z, color });
          }
        }
      }
    }
    
    // Handle: arching over the basket from left (-3,0) to right (3,0), up to hand at y=6
    for (let y = 0; y <= 6; y++) {
      voxels.push({ x: -3, y, z: 0, color: rimColor });
      voxels.push({ x: 3, y, z: 0, color: rimColor });
    }
    for (let x = -3; x <= 3; x++) {
      voxels.push({ x, y: 6, z: 0, color: rimColor });
    }
    
    // Fill interior with leafy green stems
    for (let x = -2; x <= 2; x++) {
      for (let z = -2; z <= 2; z++) {
        if (Math.abs(x) === 2 && Math.abs(z) === 2) continue;
        voxels.push({ x, y: -1, z, color: leafColor });
        voxels.push({ x, y: -2, z, color: leafColor });
      }
    }
    
    // Exquisite individual flowers peeking out!
    // Flower A: Red Tulip/Rose at front-left
    voxels.push({ x: -1, y: 0, z: -1, color: PALETTE.FLOWER_RED });
    voxels.push({ x: -1, y: 1, z: -1, color: 0xff3333 }); // petal
    voxels.push({ x: -2, y: 0, z: -1, color: PALETTE.FLOWER_YELLOW }); // center
    
    // Flower B: Blue Cornflower at back-right
    voxels.push({ x: 1, y: 0, z: 1, color: PALETTE.FLOWER_BLUE });
    voxels.push({ x: 1, y: 1, z: 1, color: 0x3399ff }); // petal
    voxels.push({ x: 0, y: 0, z: 1, color: PALETTE.FLOWER_YELLOW });
    
    // Flower C: Golden Daisy at front-right
    voxels.push({ x: 1, y: 0, z: -1, color: 0xffd700 }); // bright gold
    voxels.push({ x: 1, y: 1, z: -1, color: 0xffffff }); // white petal
    voxels.push({ x: 2, y: 0, z: -1, color: leafColor });
  }
  
  return cleanupDuplicates(voxels);
}

export function getCharacterAsset(type: CharacterType): VoxelAsset {
  const parts = generateCharacterParts(type);
  const voxels: VoxelDataSmall[] = [];
  
  // Assemble parts statically centered with correct heights using normal-sized voxels
  parts.leftLeg.forEach(v => voxels.push({ x: v.x - 2, y: v.y + 6, z: v.z, color: v.color }));
  parts.rightLeg.forEach(v => voxels.push({ x: v.x + 2, y: v.y + 6, z: v.z, color: v.color }));
  parts.torso.forEach(v => voxels.push({ x: v.x, y: v.y + 6, z: v.z, color: v.color }));
  parts.leftArm.forEach(v => voxels.push({ x: Math.floor(v.x - 4.5), y: v.y + 10, z: v.z, color: v.color }));
  parts.rightArm.forEach(v => voxels.push({ x: Math.floor(v.x + 4.5), y: v.y + 10, z: v.z, color: v.color }));
  parts.head.forEach(v => voxels.push({ x: v.x, y: v.y + 11, z: v.z, color: v.color }));

  // Add beautifully downscaled accessory voxels
  const accVoxels = generateAccessoryVoxels(type);
  if (type === 'farmer') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx + 5, y: ry + 5, z: rz, color: v.color });
    });
  } else if (type === 'miller') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx - 5, y: ry + 5, z: rz + 1, color: v.color });
    });
  } else if (type === 'villager') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx + 5, y: ry + 3, z: rz + 1, color: v.color });
    });
  }

  const cleanVoxels = cleanupDuplicates(voxels).map(v => ({
    x: v.x,
    y: v.y,
    z: v.z,
    color: v.color,
    materialType: 'opaque' as const
  }));

  let name = '';
  let description = '';
  if (type === 'farmer') {
    name = 'Voxel Farmer';
    description = 'A detailed small-scale voxel farmer tending the crop fields. Holds a highly-detailed metal hoe for weeding and sowing.';
  } else if (type === 'miller') {
    name = 'Voxel Miller';
    description = 'A blond voxel baker/miller wearing a flour-dusted apron. Holds an artisan loaf of freshly baked crusty bread with custom crust slashes.';
  } else {
    name = 'Voxel Villager';
    description = 'A sweet village girl wearing a beautiful golden-yellow dress. Holds a delicate woven basket filled with colorful fresh flowers.';
  }

  return {
    id: `character_${type}`,
    name,
    category: 'prop',
    description,
    voxels: cleanVoxels,
    dimensions: { x: 12, y: 20, z: 12 }
  };
}

export function generateCharacterVoxels(type: CharacterType): VoxelDataSmall[] {
  const parts = generateCharacterParts(type);
  const voxels: VoxelDataSmall[] = [];
  
  // Assemble parts statically centered with correct heights using normal-sized voxels
  parts.leftLeg.forEach(v => voxels.push({ x: v.x - 2, y: v.y + 6, z: v.z, color: v.color }));
  parts.rightLeg.forEach(v => voxels.push({ x: v.x + 2, y: v.y + 6, z: v.z, color: v.color }));
  parts.torso.forEach(v => voxels.push({ x: v.x, y: v.y + 6, z: v.z, color: v.color }));
  parts.leftArm.forEach(v => voxels.push({ x: Math.floor(v.x - 3.5), y: v.y + 10, z: v.z, color: v.color }));
  parts.rightArm.forEach(v => voxels.push({ x: Math.floor(v.x + 3.5), y: v.y + 10, z: v.z, color: v.color }));
  parts.head.forEach(v => voxels.push({ x: v.x, y: v.y + 11, z: v.z, color: v.color }));

  // Add beautifully downscaled accessory voxels
  const accVoxels = generateAccessoryVoxels(type);
  if (type === 'farmer') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx + 4, y: ry + 5, z: rz, color: v.color });
    });
  } else if (type === 'miller') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx - 4, y: ry + 5, z: rz + 1, color: v.color });
    });
  } else if (type === 'villager') {
    accVoxels.forEach(v => {
      const rx = Math.round(v.x / 2);
      const ry = Math.round(v.y / 2);
      const rz = Math.round(v.z / 2);
      voxels.push({ x: rx + 4, y: ry + 3, z: rz + 1, color: v.color });
    });
  }

  return cleanupDuplicates(voxels);
}

export class CharacterRenderer {
  public group: THREE.Group;
  private meshes: THREE.InstancedMesh[] = [];

  // Articulated parts groups
  private leftLegGroup: THREE.Group;
  private rightLegGroup: THREE.Group;
  private torsoGroup: THREE.Group;
  private leftArmGroup: THREE.Group;
  private rightArmGroup: THREE.Group;
  private headGroup: THREE.Group;
  
  constructor(type: CharacterType, public readonly smallVoxelSize: number = 0.1) {
    this.group = new THREE.Group();
    const parts = generateCharacterParts(type);

    // Create the articulated structural skeleton
    this.leftLegGroup = new THREE.Group();
    this.rightLegGroup = new THREE.Group();
    this.torsoGroup = new THREE.Group();
    this.leftArmGroup = new THREE.Group();
    this.rightArmGroup = new THREE.Group();
    this.headGroup = new THREE.Group();

    // Position groups at their natural assembly coordinate pivots
    this.leftLegGroup.position.set(-2 * smallVoxelSize, 6 * smallVoxelSize, 0);
    this.rightLegGroup.position.set(2 * smallVoxelSize, 6 * smallVoxelSize, 0);
    this.torsoGroup.position.set(0, 6 * smallVoxelSize, 0);
    this.leftArmGroup.position.set(-3.5 * smallVoxelSize, 10 * smallVoxelSize, 0);
    this.rightArmGroup.position.set(3.5 * smallVoxelSize, 10 * smallVoxelSize, 0);
    this.headGroup.position.set(0, 11 * smallVoxelSize, 0);

    // Build the voxel geometry meshes for each group
    this.leftLegGroup.add(this.buildPartMesh(parts.leftLeg, smallVoxelSize));
    this.rightLegGroup.add(this.buildPartMesh(parts.rightLeg, smallVoxelSize));
    this.torsoGroup.add(this.buildPartMesh(parts.torso, smallVoxelSize));
    this.leftArmGroup.add(this.buildPartMesh(parts.leftArm, smallVoxelSize));
    this.rightArmGroup.add(this.buildPartMesh(parts.rightArm, smallVoxelSize));
    this.headGroup.add(this.buildPartMesh(parts.head, smallVoxelSize));

    // Build and attach high-resolution detailed accessories with smaller voxel size (half size!)
    if (type === 'farmer') {
      const hoeVoxels = generateAccessoryVoxels('farmer');
      const hoeMesh = this.buildPartMesh(hoeVoxels, smallVoxelSize * 0.5);
      hoeMesh.position.set(0, -4.5 * smallVoxelSize, 0);
      this.rightArmGroup.add(hoeMesh);
    } else if (type === 'miller') {
      const breadVoxels = generateAccessoryVoxels('miller');
      const breadMesh = this.buildPartMesh(breadVoxels, smallVoxelSize * 0.5);
      breadMesh.position.set(0, -4.5 * smallVoxelSize, 1.5 * smallVoxelSize);
      breadMesh.rotation.set(0.2, 0.4, -0.1);
      this.leftArmGroup.add(breadMesh);
    } else if (type === 'villager') {
      const basketVoxels = generateAccessoryVoxels('villager');
      const basketMesh = this.buildPartMesh(basketVoxels, smallVoxelSize * 0.5);
      basketMesh.position.set(1.5 * smallVoxelSize, -7.0 * smallVoxelSize, 1.5 * smallVoxelSize);
      this.rightArmGroup.add(basketMesh);
    }

    // Assemble the skeleton under the main character group
    this.group.add(this.leftLegGroup);
    this.group.add(this.rightLegGroup);
    this.group.add(this.torsoGroup);
    this.group.add(this.leftArmGroup);
    this.group.add(this.rightArmGroup);
    this.group.add(this.headGroup);
  }

  private buildPartMesh(voxels: VoxelDataSmall[], smallVoxelSize: number): THREE.Group {
    const partGroup = new THREE.Group();
    const colorMap = new Map<number, VoxelDataSmall[]>();
    voxels.forEach(v => {
      if (!colorMap.has(v.color)) colorMap.set(v.color, []);
      colorMap.get(v.color)!.push(v);
    });
    
    const geometry = new THREE.BoxGeometry(smallVoxelSize, smallVoxelSize, smallVoxelSize);
    const dummy = new THREE.Object3D();
    
    colorMap.forEach((vArr, color) => {
      const mat = new THREE.MeshLambertMaterial({ color });
      const mesh = new THREE.InstancedMesh(geometry, mat, vArr.length);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      vArr.forEach((v, i) => {
        dummy.position.set(v.x * smallVoxelSize, v.y * smallVoxelSize, v.z * smallVoxelSize);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.meshes.push(mesh);
      partGroup.add(mesh);
    });
    
    return partGroup;
  }

  public update(time: number, delta: number, isMoving: boolean) {
    // Dynamic walker animation frequencies and bounds
    const speedFactor = 5.0; // Paced, casual stroll matching peaceful scenery
    const maxSwing = 0.28; // Subtle, gentle leg movement (~16 degrees max)
    
    if (isMoving) {
      const angle = Math.sin(time * 0.001 * speedFactor) * maxSwing;
      
      // Left and right legs swing in perfect counter-phase
      this.leftLegGroup.rotation.x = angle;
      this.rightLegGroup.rotation.x = -angle;
      
      // Arms swing counter-phase to the leg on their side but with a dampened amplitude (to carry accessories gently)
      this.leftArmGroup.rotation.x = -angle * 0.45;
      this.rightArmGroup.rotation.x = angle * 0.45;
      
      // Chibi swing swagger (subtle side flare)
      this.leftArmGroup.rotation.z = -0.08 - Math.abs(angle) * 0.05;
      this.rightArmGroup.rotation.z = 0.08 + Math.abs(angle) * 0.05;
      
      // Soft, minimal head bobbing
      this.headGroup.rotation.y = Math.sin(time * 0.001 * speedFactor * 0.5) * 0.04;
      this.headGroup.rotation.z = Math.cos(time * 0.001 * speedFactor * 0.5) * 0.015;
      
      // Slight forward torso lean with minimal motion bobbing
      this.torsoGroup.rotation.x = 0.02 + Math.sin(time * 0.001 * speedFactor * 2.0) * 0.005;
    } else {
      // Return smoothly to static standing position when paused
      const lerpSpeed = 6.0 * delta;
      
      this.leftLegGroup.rotation.x = THREE.MathUtils.lerp(this.leftLegGroup.rotation.x, 0, lerpSpeed);
      this.rightLegGroup.rotation.x = THREE.MathUtils.lerp(this.rightLegGroup.rotation.x, 0, lerpSpeed);
      
      this.leftArmGroup.rotation.x = THREE.MathUtils.lerp(this.leftArmGroup.rotation.x, 0, lerpSpeed);
      this.rightArmGroup.rotation.x = THREE.MathUtils.lerp(this.rightArmGroup.rotation.x, 0, lerpSpeed);
      
      this.leftArmGroup.rotation.z = THREE.MathUtils.lerp(this.leftArmGroup.rotation.z, -0.05, lerpSpeed);
      this.rightArmGroup.rotation.z = THREE.MathUtils.lerp(this.rightArmGroup.rotation.z, 0.05, lerpSpeed);
      
      this.headGroup.rotation.x = THREE.MathUtils.lerp(this.headGroup.rotation.x, 0, lerpSpeed);
      this.headGroup.rotation.y = THREE.MathUtils.lerp(this.headGroup.rotation.y, 0, lerpSpeed);
      this.headGroup.rotation.z = THREE.MathUtils.lerp(this.headGroup.rotation.z, 0, lerpSpeed);
      this.torsoGroup.rotation.x = THREE.MathUtils.lerp(this.torsoGroup.rotation.x, 0, lerpSpeed);
    }
  }

  public dispose() {
    this.meshes.forEach(m => {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    });
  }
}
